/**
 * The /admin gate.
 *
 * Ported from `app/admin/layout.tsx`. **Since W1 it asks the backend** (`GET /access/me`)
 * rather than reading the `role: "admin"` claim itself: under Nexus nobody carries that claim,
 * and the answer comes from the same function `require_admin` uses. It keeps the original's two
 * load-bearing decisions:
 *
 * - **It force-refreshes the token before asking.** Under the allowlist the backend still reads
 *   the claim from the token it is sent, and an admin whose role was granted during this
 *   session holds a token minted before the claim existed. Asked with that token, the gate
 *   would stay shut for a real admin. Refreshing through the provider also updates the token
 *   `request()` uses, so the two cannot disagree.
 * - **It renders nothing until the answer is in.** This is convenience, not security:
 *   `require_admin` on every admin route is the enforcement, and being wrong here reveals a
 *   page shell, never data. But showing the shell to a non-admin means showing them a screen
 *   of errors, which is worse than showing them nothing.
 *
 * Unlike the Next.js version a denied caller is told so rather than silently redirected home.
 * A bounce with no explanation is indistinguishable from a broken link, and the one thing that
 * fixes this state — ask an existing admin, then sign out and back in — is worth saying.
 */

import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { ApiClientError } from "@/api/client";
import { fetchMyAccess } from "@/api/queries";
import { useAuth } from "@/auth/AuthProvider";
import { CentredSpinner, RequireAuth } from "@/components/RequireAuth";
import { NOT_ADMIN_MESSAGE } from "@/constants/messages";

/** The access check's outcome, tagged with whose it was. See `AdminAccessGate`. */
interface ResolvedAccess {
  uid: string;
  isAdmin: boolean;
  /**
   * Set when the question could not be answered — a Nexus outage is a 503 from the backend.
   * Shown rather than folded into "not an admin", which would send a real admin off to ask for
   * access they already have.
   */
  error?: string;
}

const CHECK_FAILED_MESSAGE =
  "Your access could not be checked right now. Please try again shortly.";

export function RequireAdmin({ children }: { children: ReactNode }) {
  // Nested rather than duplicated: the signed-out case is already handled, correctly, once.
  return (
    <RequireAuth>
      <AdminAccessGate>{children}</AdminAccessGate>
    </RequireAuth>
  );
}

function AdminAccessGate({ children }: { children: ReactNode }) {
  const { user, refreshToken } = useAuth();
  const [resolved, setResolved] = useState<ResolvedAccess | null>(null);

  useEffect(() => {
    if (!user) return;

    let active = true;

    void (async () => {
      try {
        await refreshToken();
        // The token just refreshed above — cached, so no second round trip to Firebase.
        const access = await fetchMyAccess(await user.getIdToken());
        if (active) setResolved({ uid: user.uid, isAdmin: access.isAdmin });
      } catch (error) {
        // Not an admin, and not silently: the likely causes are a revoked session, a network
        // failure or Nexus being down, and none of those is "you are not an admin". The
        // backend's message is client-safe by construction; anything else gets a generic one.
        console.error("[admin] failed to check admin access:", error);
        const message = error instanceof ApiClientError ? error.message : CHECK_FAILED_MESSAGE;
        if (active) setResolved({ uid: user.uid, isAdmin: false, error: message });
      }
    })();

    return () => {
      active = false;
    };
  }, [user, refreshToken]);

  /*
   * The outcome is tagged with the uid it was read for, and "still checking" is derived from
   * that rather than written back into state when the effect starts. Resetting on entry would
   * be a synchronous setState inside an effect — a cascading render — and it would also be
   * the weaker check: a switch to a different account is caught here by the uid not matching,
   * whether or not the reset had run yet.
   */
  if (!user || resolved?.uid !== user.uid) {
    return <CentredSpinner label="Checking your access…" />;
  }
  if (resolved.error !== undefined) return <AccessCheckFailedNotice message={resolved.error} />;
  if (!resolved.isAdmin) return <NotAdminNotice />;
  return <>{children}</>;
}

function NotAdminNotice() {
  return (
    <Box className="mx-auto max-w-md py-12">
      <Alert severity="info" variant="outlined" sx={{ backgroundColor: "rgba(255,255,255,0.9)" }}>
        <AlertTitle>This area is for administrators</AlertTitle>
        <Typography variant="body2">{NOT_ADMIN_MESSAGE}</Typography>
      </Alert>
    </Box>
  );
}

function AccessCheckFailedNotice({ message }: { message: string }) {
  return (
    <Box className="mx-auto max-w-md py-12">
      <Alert severity="warning" variant="outlined" sx={{ backgroundColor: "rgba(255,255,255,0.9)" }}>
        <AlertTitle>Access could not be checked</AlertTitle>
        <Typography variant="body2">{message}</Typography>
      </Alert>
    </Box>
  );
}
